import React from 'react'
import { Hand, Redo2, Undo2, ZoomIn, ZoomOut } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { TooltipProvider } from '@/components/ui/tooltip'
import { cn } from '@/utils'
import { ZoomLevelControl } from '@/components/app/layout/zoom-level-control'
import { formatAriaKeyshortcuts } from '@/lib/keyboard/keyboard-shortcuts'
import { useEditorCommandState } from '@/contexts/editor-command-state-context'
import { useEditorCommands } from '@/contexts/editor-commands-context'
import { useEditorTools } from '@/contexts/editor-tools-context'
import { IconButton } from './components/icon-button'
import { ShortcutTooltip } from './components/shortcut-tooltip'

/** The greyed-out look of a disabled button, for one that must stay focusable (see Undo/Redo). */
const UNAVAILABLE_BUTTON_CLASS_NAME =
  'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-current'

type NavBarProps = {
  centerTitle?: React.ReactNode
  /** Brief toolbar press affordance when zoom is triggered from keyboard (matches `active:translate-y-0.5`). */
  zoomToolbarKeyFlash?: 'in' | 'out' | null
  zoomInDisabled?: boolean
  zoomOutDisabled?: boolean
  /** Effective zoom as percent (10–800) for the zoom control display. */
  zoomPercent: number
  fitToSpreadDisabled?: boolean
  zoomToSelectionDisabled?: boolean
  zoomMinPercent?: number
  zoomMaxPercent?: number
  className?: string
  height?: number // px height, default 52
  /** When set, zoom preset menu aligns its right edge to this element’s left (the right panel, so
   *  the menu stays clear of it whether it is open or collapsed). */
  zoomDropdownAlignRef?: React.RefObject<HTMLElement | null>
}

function NavBar({
  centerTitle,
  zoomToolbarKeyFlash = null,
  zoomInDisabled = false,
  zoomOutDisabled = false,
  zoomPercent,
  fitToSpreadDisabled = false,
  zoomToSelectionDisabled = true,
  zoomMinPercent = 10,
  zoomMaxPercent = 800,
  className,
  height = 52,
  zoomDropdownAlignRef,
}: NavBarProps) {
  const commands = useEditorCommands()
  const { canUndo, canRedo } = useEditorCommandState()
  const { isPanModeActive: isHandActive, togglePanMode: onHandToggle } = useEditorTools()
  const onUndo = canUndo ? commands.undo : undefined
  const onRedo = canRedo ? commands.redo : undefined
  const {
    zoomIn: onZoomIn,
    zoomOut: onZoomOut,
    commitZoomPercent: onZoomPercentCommit,
    actualSize: onActualSize,
    zoomFit: onZoomFit,
    zoomToSelection: onZoomToSelection,
  } = commands
  const zoomInAriaKeyshortcuts = formatAriaKeyshortcuts('zoomIn')
  const zoomOutAriaKeyshortcuts = formatAriaKeyshortcuts('zoomOut')
  const undoAriaKeyshortcuts = formatAriaKeyshortcuts('undo')
  const redoAriaKeyshortcuts = formatAriaKeyshortcuts('redo')
  const panAriaKeyshortcuts = formatAriaKeyshortcuts('pan')

  return (
    <TooltipProvider delayDuration={0}>
      <Card
        role="toolbar"
        aria-label="Editor toolbar"
        className={cn(
          'rounded-none flex w-full items-center gap-4 px-3 py-2 border-t-0 border-x-0',
          'border-b border-border',
          className
        )}
        style={{ height }}
      >
        {/* data-text-edit-keepalive: clicking Undo/Redo blurs the shape-text editor's textarea
            first (native focus-change behavior), and that blur handler exits the active edit
            session unless the newly-focused element sits inside a keepalive zone — the same
            mechanism the Type panel's own controls already rely on. Without it, undoing/redoing a
            style change on a highlighted run always exited edit mode and lost the selection before
            `onUndo`/`onRedo` ever ran, even though the handlers themselves now preserve it. */}
        <div className="flex items-center gap-2" data-text-edit-keepalive="">
          {/* Unavailable when there's nothing to undo/redo — marked `aria-disabled` and greyed out,
              matching Edit → Undo/Redo — but NOT the `disabled` attribute: a disabled button can't
              take focus, so clicking one mid text edit would move focus to <body> and the editor's
              blur handler would end the edit session. Kept focusable, the click lands inside this
              keepalive zone and the session stays open; with no handler it does nothing. */}
          {/* The tooltip shows while unavailable too: the buttons stay focusable, so it still
              tells the user what the greyed-out icon is and which keys do the same. */}
          <ShortcutTooltip label="Undo" shortcutKey="undo" comboIndex={0}>
            <IconButton
              ariaLabel="Undo"
              ariaKeyshortcuts={undoAriaKeyshortcuts}
              onClick={onUndo}
              aria-disabled={!onUndo || undefined}
              className={cn(!onUndo && UNAVAILABLE_BUTTON_CLASS_NAME)}
            >
              <Undo2 className="h-4 w-4" />
            </IconButton>
          </ShortcutTooltip>
          <ShortcutTooltip label="Redo" shortcutKey="redo" comboIndex={0}>
            <IconButton
              ariaLabel="Redo"
              ariaKeyshortcuts={redoAriaKeyshortcuts}
              onClick={onRedo}
              aria-disabled={!onRedo || undefined}
              className={cn(!onRedo && UNAVAILABLE_BUTTON_CLASS_NAME)}
            >
              <Redo2 className="h-4 w-4" />
            </IconButton>
          </ShortcutTooltip>
        </div>

        <div className="flex min-w-0 flex-1 justify-center px-2">
          <div className="max-w-[70ch] truncate text-xs text-muted-foreground">{centerTitle}</div>
        </div>

        <div className="flex items-center gap-3">
          <ShortcutTooltip label="Pan" shortcutKey="pan" comboIndex={0}>
            <IconButton
              ariaLabel="Pan"
              ariaKeyshortcuts={panAriaKeyshortcuts}
              onClick={onHandToggle}
              isActive={isHandActive}
              className="cursor-pointer"
            >
              <Hand className="h-4 w-4" />
            </IconButton>
          </ShortcutTooltip>
          <div className="flex items-center gap-2" role="group" aria-label="Zoom">
            <ShortcutTooltip label="Zoom out" shortcutKey="zoomOut" comboIndex={1}>
              <span className="inline-flex">
                <IconButton
                  ariaLabel="Zoom out"
                  ariaKeyshortcuts={zoomOutAriaKeyshortcuts}
                  onClick={onZoomOut}
                  disabled={zoomOutDisabled}
                  className={zoomToolbarKeyFlash === 'out' ? 'translate-y-0.5' : undefined}
                >
                  <ZoomOut className="h-4 w-4" />
                </IconButton>
              </span>
            </ShortcutTooltip>
            <ZoomLevelControl
              zoomPercent={zoomPercent}
              onZoomPercentCommit={onZoomPercentCommit}
              onActualSize={onActualSize}
              onZoomFit={onZoomFit}
              fitToSpreadDisabled={fitToSpreadDisabled}
              onZoomToSelection={onZoomToSelection}
              zoomToSelectionDisabled={zoomToSelectionDisabled}
              minPercent={zoomMinPercent}
              maxPercent={zoomMaxPercent}
              dropdownAlignRef={zoomDropdownAlignRef}
              className="hidden md:inline-flex"
            />
            <ShortcutTooltip label="Zoom in" shortcutKey="zoomIn" comboIndex={2}>
              <span className="inline-flex">
                <IconButton
                  ariaLabel="Zoom in"
                  ariaKeyshortcuts={zoomInAriaKeyshortcuts}
                  onClick={onZoomIn}
                  disabled={zoomInDisabled}
                  className={zoomToolbarKeyFlash === 'in' ? 'translate-y-0.5' : undefined}
                >
                  <ZoomIn className="h-4 w-4" />
                </IconButton>
              </span>
            </ShortcutTooltip>
          </div>
        </div>
      </Card>
    </TooltipProvider>
  )
}

export default React.memo(NavBar)
